import { config } from '@/config';
import { generateRecaptchaTokenSafe } from '@/lib/recaptcha';
import { captureApiError } from '@/lib/sentry';
import type { RecaptchaAction } from '@/types/recaptcha';

const beehiveUrl = (path: string) => {
  const beehvieUrl = config.beehiveUrl;
  const apiUrl = `${beehvieUrl}/${path}`;
  return apiUrl;
};

const buildFromData = (data: Map<string, string>) => {
  return JSON.stringify(Object.fromEntries(data));
};

async function buildFetchOptions(
  method: 'GET' | 'POST',
  body?: string | null,
  recaptchaAction?: RecaptchaAction
): Promise<RequestInit> {
  const defaultHeaders: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  if (recaptchaAction) {
    try {
      const recaptchaToken = await generateRecaptchaTokenSafe(recaptchaAction, {
        useCache: false,
      });
      if (recaptchaToken) {
        defaultHeaders['x-recaptcha-token'] = recaptchaToken;
      }
    } catch (error) {
      console.warn('Failed to generate reCAPTCHA token:', error);
    }
  }

  const options: RequestInit = {
    method,
    headers: defaultHeaders,
    credentials: 'include',
  };

  if (body && method === 'POST') {
    options.body = body;
  }

  return options;
}

async function handleFetchResponse(
  response: Response,
  endpoint: string,
  method: string
): Promise<Response> {
  if (!response.ok) {
    const errorBody = await response
      .clone()
      .json()
      .catch(() => ({ message: 'An unknown error occurred' }));

    // Capture 5xx server errors to Sentry
    if (response.status >= 500) {
      const error = new Error(
        `Beehive server error: ${response.status} ${response.statusText}`
      );
      captureApiError(error, {
        endpoint,
        method,
        statusCode: response.status,
        responseBody: errorBody,
      });
    }

    throw new Error(errorBody.message || `API error: ${response.status}`);
  }
  return response;
}

export const beehiveFetcher = async (
  path: string,
  { arg }: { arg: Map<string, string> }
) => {
  const beehiveEndpoint = beehiveUrl(path);
  const body = buildFromData(arg);
  const options = await buildFetchOptions('POST', body);

  const response = await fetch(beehiveEndpoint, options);
  return handleFetchResponse(response, beehiveEndpoint, 'POST').then((res) =>
    res.json()
  );
};

export const beehiveGetFetcher = async (path: string) => {
  const beehiveEndpoint = beehiveUrl(path);
  const options = await buildFetchOptions('GET');

  const response = await fetch(beehiveEndpoint, options);
  return handleFetchResponse(response, beehiveEndpoint, 'GET').then((res) =>
    res.json()
  );
};

export const beehivePostApiHelper = async <T = unknown>(
  path: string,
  body: Record<string, unknown>,
  recaptchaAction?: RecaptchaAction
): Promise<T> => {
  const beehiveEndpoint = beehiveUrl(path);
  const bodyString = JSON.stringify(body);
  const options = await buildFetchOptions('POST', bodyString, recaptchaAction);

  const response = await fetch(beehiveEndpoint, options);
  return handleFetchResponse(response, beehiveEndpoint, 'POST').then((res) =>
    res.json()
  );
};
